import { ConnectedStandardSolanaWallet } from '@privy-io/react-auth/solana';
import {
	BaseMessageSignerWalletAdapter,
	WalletName,
	WalletReadyState,
	isVersionedTransaction,
} from '@solana/wallet-adapter-base';
import {
	PublicKey,
	Transaction,
	TransactionVersion,
	VersionedTransaction,
} from '@solana/web3.js';
import {
	DRIFT_CUSTOM_WALLET_ICONS,
	DRIFT_CUSTOM_WALLET_OPTIONS,
} from 'src/constants/wallets';
import Env from 'src/environmentVariables/EnvironmentVariables';

export class PrivyNativeAdapter extends BaseMessageSignerWalletAdapter {
	url = '';
	readyState: WalletReadyState = WalletReadyState.Installed;
	connecting = false;
	supportedTransactionVersions?: ReadonlySet<TransactionVersion>;

	public name =
		DRIFT_CUSTOM_WALLET_OPTIONS.PRIVY_NATIVE_WALLET_ADAPTER_NAME as WalletName;
	public icon = DRIFT_CUSTOM_WALLET_ICONS.PRIVY_NATIVE_WALLET_ADAPTER.dark;
	public publicKey: PublicKey | null = null;

	private _connected = false;
	private chain: 'solana:mainnet' | 'solana:devnet';

	public get connected() {
		return this._connected;
	}

	public set connected(value) {
		this._connected = value;
	}

	public constructor(private wallet: ConnectedStandardSolanaWallet) {
		super();
		this.publicKey = new PublicKey(wallet.address);
		this._connected = true;
		this.chain =
			Env.sdkEnv === 'mainnet-beta' ? 'solana:mainnet' : 'solana:devnet';
	}

	public async signTransaction<T extends Transaction | VersionedTransaction>(
		tx: T
	): Promise<T> {
		const isVersionedTx = isVersionedTransaction(tx);

		// Apparently legacy `Transaction.serialize()` has two signature-related
		// checks enabled by default: `requireAllSignatures` and `verifySignatures`,
		// with both defaulting to `true`. And when serializing an unsigned transaction
		// for signing, both need to be disabled.
		const serializedTransaction = isVersionedTx
			? tx.serialize()
			: tx.serialize({ requireAllSignatures: false, verifySignatures: false });

		const signTxnResult = await this.wallet.signTransaction({
			transaction: serializedTransaction as Uint8Array,
			chain: this.chain,
		});

		const { signedTransaction } = signTxnResult;

		if (isVersionedTx) {
			return VersionedTransaction.deserialize(signedTransaction) as T;
		}

		return Transaction.from(signedTransaction) as T;
	}

	public async signAllTransactions<
		T extends Transaction | VersionedTransaction,
	>(txes: T[]): Promise<T[]> {
		return Promise.all(txes.map((tx) => this.signTransaction(tx)));
	}

	public async signMessage(message: Uint8Array): Promise<Uint8Array> {
		const signMessageResult = await this.wallet.signMessage({ message });
		return signMessageResult.signature;
	}

	public connect = async (): Promise<void> => {
		this._connected = true;
		if (!this.publicKey) {
			this.publicKey = new PublicKey(this.wallet.address);
		}
		this.emit('connect', this.publicKey);
	};

	public disconnect = async (): Promise<void> => {
		await this.wallet.disconnect();
		this._connected = false;
		this.publicKey = null;
		this.emit('disconnect');
	};
}

const createPrivyNativeWalletAdapter = (
	wallet: ConnectedStandardSolanaWallet
) => {
	return new PrivyNativeAdapter(wallet);
};

export default createPrivyNativeWalletAdapter;
